import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';

/**
 * "Back to the list" that agrees with the browser's Back button.
 *
 * A return link that always pushes its target breaks the history in two ways:
 * the list comes back without the filters, sort or view it was left with, and
 * the browser's Back from there lands on the record again - so the two back
 * controls on screen walk the user in a circle.
 *
 * `useReturnTo(target)` steps back instead whenever the entry before this one
 * is the target screen (same pathname, any query), which restores it exactly
 * as it was left. Only when the user did not come from there - a deep link, a
 * new tab, a hop from another module - does it navigate to `target`.
 *
 * It knows the previous entry because the shell calls `useTrackHistory` on
 * every location change, keyed by the `idx` React Router writes into each
 * history entry. The record survives a reload through sessionStorage, which
 * is per tab, as browser history is.
 */

interface Visited {
  pathname: string;
  search: string;
}

const STORAGE_KEY = 'portal.historyTrail';
const MAX_ENTRIES = 100;

let trail: Record<number, Visited> = readTrail();

function readTrail(): Record<number, Visited> {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? '{}') as Record<number, Visited>;
  } catch {
    return {};
  }
}

function writeTrail(): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(trail));
  } catch {
    /* storage unavailable - the in-memory trail still serves this page load */
  }
}

function currentIndex(): number | null {
  const idx = (window.history.state as { idx?: unknown } | null)?.idx;

  return typeof idx === 'number' ? idx : null;
}

/** The entry `steps` before the current one, if this tab has visited it. */
export function previousEntry(steps = 1): Visited | null {
  const idx = currentIndex();

  return idx === null ? null : trail[idx - steps] ?? null;
}

/**
 * Records each location against its history index. Called once, by the shell.
 *
 * A push writes a new index and drops everything after it, since those
 * forward entries no longer exist in the browser either.
 */
export function useTrackHistory(): void {
  const location = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    const idx = currentIndex();

    if (idx === null) {
      return;
    }

    for (const key of Object.keys(trail).map(Number)) {
      const forwardGone = navigationType === 'PUSH' && key > idx;

      if (forwardGone || key < idx - MAX_ENTRIES) {
        delete trail[key];
      }
    }

    trail[idx] = { pathname: location.pathname, search: location.search };
    writeTrail();
  }, [location, navigationType]);
}

/** Strips a trailing slash so `/assignments/` and `/assignments` are the same screen. */
function normalise(pathname: string): string {
  return pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
}

export interface ReturnOptions {
  /** Replace the current entry when navigating forward - for leaving a record that no longer exists. */
  replace?: boolean;
}

/**
 * Returns a function that goes back to `target` - by stepping back when that
 * is where the user came from, and by navigating there otherwise.
 */
export function useReturnTo(target: string): (options?: ReturnOptions) => void {
  const navigate = useNavigate();

  return useCallback(
    (options?: ReturnOptions) => {
      const previous = previousEntry();
      const targetPath = normalise(new URL(target, window.location.origin).pathname);

      if (previous && normalise(previous.pathname) === targetPath) {
        navigate(-1);
        return;
      }

      navigate(target, { replace: options?.replace });
    },
    [navigate, target]
  );
}

/**
 * `navigate`, except that going to the URL already on screen replaces the
 * entry instead of pushing a copy of it.
 *
 * `<Link>` already does this; the imperative `navigate` does not. Without it,
 * clicking the nav row for the page you are on - or the activity section
 * already open - stacks identical entries, and the next Back appears to do
 * nothing.
 */
export function useNavigateTo(): (to: string) => void {
  const navigate = useNavigate();
  const location = useLocation();

  return useCallback(
    (to: string) => {
      const target = new URL(to, window.location.origin);
      const same =
        normalise(target.pathname) === normalise(location.pathname) && target.search === location.search;

      navigate(to, { replace: same });
    },
    [navigate, location.pathname, location.search]
  );
}
